// Copyright 2017 Sorama B.V.

import { GenericMeasurement, ProjectDTO } from "datatypes/SoramaWebApi";
import { StatusItem } from "datatypes/Status";
import { IPagedRequest } from "libraries/WebRequestHandler/IWebRequestHandler";

export abstract class IProjectManagementService {
    /**
     * Current Project, this property is read-only
     */
    readonly currentProject: StatusItem<ProjectDTO>;

    /**
     * All measurements the user has access to within the currently selected project, this property is read-only
     */
    readonly measurements: StatusItem<GenericMeasurement[]>;

    /**
     * Gets the current projects accessible by this user
     */
    public abstract getProjects(odataQueryFilter?: string): IPagedRequest<ProjectDTO>;

    /**
     * Sets the current Project to the Project with the specified Id.
     * @param projectId The Id of the Project to set as current.
     * @throws {Error} if the projectId is not the Id of an existing Project.
     */
    public abstract setCurrentProject(projectId: string): Promise<void>;

    /**
     * Sets the current Project to the ProjectDTO, with its properties as a placeholder.
     * Allows users of the project management service to use projectId immediately.
     *  E.g. to fetch measurements in the meantime.
     * Fetches it from the backend sync the project by its id in case it has changed.
     * @param project The ProjectDTO to set as current.
     * @throws {Error} if the projectId is not the Id of an existing Project.
     */
    public abstract setCurrentProjectWithPlaceholder(project: ProjectDTO): Promise<void>;

    /**
     * Gets the currently selected projected.
     */
    public abstract getCurrentProject(): Promise<ProjectDTO>;

    /**
     * Creates a new project.
     * @param projectName The name of the new project.
     * @param projectDescription (optional) The description of the new project.
     */
    public abstract createProject(projectName: string, projectDescription?: string): Promise<ProjectDTO>;

    /**
     * Create a measurement from an existing recording group in the currently selected project
     * @param recordingGroupId Id of the recordinggroup to create a measurement from
     */
    public abstract createMeasurement(recordingGroupId: string): Promise<string>;

    /**
     * Updates the Project with the specified Id.
     * @param projectId The Id of the Project to update.
     * @param newProperties The properties to be updated.
     */
    public abstract updateProject(projectId: string, newProperties: unknown): Promise<ProjectDTO>;

    /**
     * Deletes the Project with the specified Id.
     * @param projectId The Id of the Project to delete.
     */
    public abstract deleteProject(projectId: string): Promise<void>;

    /**
     * Refreshes the Measurements of the current selected Project.
     */
    public abstract refreshMeasurements(): Promise<void>;

    /**
     * Try to refresh, but gracefully accept failure
     */
    public abstract tryRefreshMeasurements(): Promise<void>;

    /**
     * Gets a list of projectIds of the projects the user has favorited
     */
    public abstract getFavoriteProjectIds(): Promise<string[]>;

    /**
     * Gets a boolean denoting if the project is favorited
     */
    public abstract getProjectFavorited(projectId: string): Promise<boolean>;

    /**
     * Sets a boolean denoting if the project is favorited
     */
    public abstract setProjectFavorited(projectId: string, isFavorite?: boolean): Promise<void>;
}
